import { type FieldValue } from "@components/job_post/field";
import type { EeocQuestion, Question } from "@/types/jben/job_post";
import type { WhoamiProfile } from "@/queries/use_whoami";
import { type EducationAutofiller } from "./education_autofiller";
import { type EmploymentAutofiller } from "./employment_autofiller";

const WEBSITE_FIELD_LABELS = Object.freeze({
  github: ["GitHub", "GitHub Profile", "GitHub Link", "GitHub URL"],
  linkedin: ["LinkedIn", "LinkedIn Profile"],
  portfolio: [
    "Portfolio",
    "Website/Portfolio",
    "Website / Portfolio",
    "Portfolio link",
    "Portfolio URL",
  ],
  website: ["Website"],
});

export class Autofiller {
  private answers: Record<string, FieldValue | null>;
  private setAnswers: (answers: Record<string, FieldValue>) => void;
  private questions: Question[];
  private eeocQuestions: EeocQuestion[];
  private educationAutofiller: EducationAutofiller;
  private employmentAutofiller: EmploymentAutofiller;

  constructor(
    answers: Record<string, FieldValue>,
    setAnswers: (answers: Record<string, FieldValue>) => void,
    questions: Question[],
    eeocQuestions: EeocQuestion[],
    educationAutofiller: EducationAutofiller,
    employmentAutofiller: EmploymentAutofiller,
  ) {
    this.answers = answers;
    this.setAnswers = setAnswers;
    this.questions = questions;
    this.eeocQuestions = eeocQuestions;
    this.educationAutofiller = educationAutofiller;
    this.employmentAutofiller = employmentAutofiller;
  }

  async fill(profile: WhoamiProfile): Promise<void> {
    this.fillAnswers(profile);
    this.employmentAutofiller.fill(profile);
    await this.educationAutofiller.fill(profile);
  }

  private fillAnswers(profile: WhoamiProfile): void {
    const newAnswers = {
      ...this.answers,
      first_name: this.chooseAnswer("first_name", profile.firstName),
      last_name: this.chooseAnswer("last_name", profile.lastName),
      email: this.chooseAnswer("email", profile.email),
      phone: this.chooseAnswer("phone", profile.phoneNumber),
      resume: this.chooseResumeAnswer(profile.resume),
    } as typeof this.answers;

    if (this.hasLocationField) {
      newAnswers.location = this.chooseAnswer("location", profile.location?.displayName);
      newAnswers.latitude = this.chooseAnswer("latitude", profile.location?.latitude.toString());
      newAnswers.longitude = this.chooseAnswer("longitude", profile.location?.longitude.toString());
      newAnswers.country_short_name = this.chooseAnswer(
        "country_short_name",
        profile.location?.countryShortName,
      );
    }

    if (this.hasPreferredNameField) {
      newAnswers.preferred_name = this.chooseAnswer("preferred_name", profile.preferredName);
    }

    this.fillWebsites(profile, newAnswers);
    this.fillEeoc(profile, newAnswers);

    this.setAnswers(newAnswers as Record<string, FieldValue>);
  }

  // Respects existing user input: keeps any non-empty string or object answer
  // already in the form rather than overwriting it with the profile value.
  private chooseAnswer(key: string, autofillAnswer?: string | null): FieldValue {
    const existingAnswer = this.answers[key];

    if (typeof existingAnswer === "object" && existingAnswer != null) {
      return existingAnswer;
    }

    return (existingAnswer as string | undefined)?.trim() || autofillAnswer || "";
  }

  private fillWebsites(
    profile: WhoamiProfile,
    newAnswers: Record<string, FieldValue | null>,
  ): void {
    const websites = profile.websites;
    if (!websites) return;

    (Object.keys(websites) as (keyof typeof websites)[]).forEach((key) => {
      const question = this.findQuestionByLabels(WEBSITE_FIELD_LABELS[key]);
      if (question) {
        const questionKey = question.fields[0].name;
        newAnswers[questionKey] = this.chooseAnswer(questionKey, websites[key]);
      }
    });
  }

  private fillEeoc(profile: WhoamiProfile, newAnswers: Record<string, FieldValue | null>): void {
    const selfIdentification = profile.selfIdentification;
    if (!selfIdentification) return;

    this.eeocQuestions.forEach((question) => {
      const field = question.fields[0];

      switch (field.name) {
        case "disability_status":
          newAnswers.disability_status = this.chooseAnswer(
            "disability_status",
            selfIdentification.disabilityStatus?.toString(),
          );
          break;
        case "gender":
          newAnswers.gender = this.chooseAnswer("gender", selfIdentification.gender?.toString());
          break;
        case "race":
          newAnswers.race = this.chooseAnswer("race", selfIdentification.race?.toString());
          break;
        case "veteran_status":
          newAnswers.veteran_status = this.chooseAnswer(
            "veteran_status",
            selfIdentification.veteranStatus?.toString(),
          );
          break;
      }
    });
  }

  private findQuestionByLabels(labels: readonly string[]): Question | undefined {
    const lowercaseLabels = labels.map((l) => l.toLowerCase());
    return this.questions.find((q) => lowercaseLabels.includes(q.label.toLowerCase()));
  }

  private chooseResumeAnswer(resume: WhoamiProfile["resume"]): FieldValue {
    if (!resume) {
      return this.answers.resume as FieldValue;
    }

    if (this.answers.resume || this.answers.resume_text) {
      return this.answers.resume as FieldValue;
    }

    // Add expiresAt (7 days) so the file-expiry timer in the form works correctly.
    return { ...resume, expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 };
  }

  private get hasLocationField(): boolean {
    return this.questions.some((q) => q.fields[0]?.name === "candidate_location");
  }

  private get hasPreferredNameField(): boolean {
    return this.questions.some((q) => q.fields[0]?.name === "preferred_name");
  }
}
